import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Loader2 } from "lucide-react";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { CalendlyInline } from "@/components/booking/CalendlyInline";
import { createSubmissionAttempt, trackLeadOnce } from "@/lib/submission";
import {
  AD_BUDGETS, ATTRIBUTION_KEYS, CHANNELS, GOALS_MAX, PRIMARY_NEEDS, REVENUES, STARTING_POINTS, STEP_ONE_FIELDS, STEP_TWO_FIELDS,
  buildIntakeSchema, normalizeWebsite, type IntakeAnswers,
} from "../../../supabase/functions/_shared/intake-schema";
import "./intake.css";

const schema = buildIntakeSchema(z);
const empty: IntakeAnswers = { startingPoint: "", company: "", website: "", primaryNeed: "", adBudget: "", revenue: "", channels: [], heardFrom: "", goals: "", firstName: "", lastName: "", email: "", phone: "" };
type Errors = Partial<Record<keyof IntakeAnswers, string>>;

function validate(a: IntakeAnswers, fields: readonly string[]): Errors {
  const r = schema.safeParse({ ...a, website: normalizeWebsite(a.website) });
  if (r.success) return {};
  const out: Errors = {};
  for (const issue of r.error.issues) { const k = issue.path[0] as keyof IntakeAnswers; if (fields.includes(k) && !out[k]) out[k] = issue.message; }
  return out;
}

function attribution(): Record<string, string> {
  const p = new URLSearchParams(window.location.search); const out: Record<string, string> = {};
  for (const k of ATTRIBUTION_KEYS) { const v = p.get(k)?.trim(); if (v) out[k] = v.slice(0, 300); }
  return out;
}

function Choice({ name, options, value, onChange, error, label, multi = false }: { name: keyof IntakeAnswers; options: readonly string[]; value: string | string[]; onChange: (v: string) => void; error?: string; label: string; multi?: boolean }) {
  const selected = (o: string) => Array.isArray(value) ? value.includes(o) : value === o;
  return <fieldset className="iq-field" aria-invalid={!!error} aria-describedby={error ? `iq-${name}-err` : undefined}>
    <legend>{label}{multi && <span className="iq-hint"> · choose all that apply</span>}</legend>
    <div className="iq-chips" id={`iq-${name}`} tabIndex={-1}>{options.map(o => <label key={o} className={`iq-chip ${selected(o) ? "is-on" : ""}`}>
      <input type={multi ? "checkbox" : "radio"} name={name} value={o} checked={selected(o)} onChange={() => onChange(o)} />{o}{selected(o) && <Check size={14} aria-hidden="true" />}
    </label>)}</div>
    {error && <p className="iq-error" id={`iq-${name}-err`}>{error}</p>}
  </fieldset>;
}

function Text({ name, label, value, onChange, error, optional, type = "text", autoComplete, placeholder, inputMode }: { name: keyof IntakeAnswers; label: string; value: string; onChange: (v: string) => void; error?: string; optional?: boolean; type?: string; autoComplete?: string; placeholder?: string; inputMode?: "url" | "email" | "tel" | "text" }) {
  return <div className="iq-field"><label htmlFor={`iq-${name}`}>{label}{optional && <span className="iq-hint"> · optional</span>}</label>
    <input id={`iq-${name}`} type={type} value={value} onChange={e => onChange(e.target.value)} autoComplete={autoComplete} placeholder={placeholder} inputMode={inputMode}
      aria-invalid={!!error} aria-describedby={error ? `iq-${name}-err` : undefined} maxLength={300} />
    {error && <p className="iq-error" id={`iq-${name}-err`}>{error}</p>}</div>;
}

function Select({ name, label, options, value, onChange, error }: { name: keyof IntakeAnswers; label: string; options: readonly string[]; value: string; onChange: (v: string) => void; error?: string }) {
  return <div className="iq-field"><label htmlFor={`iq-${name}`}>{label}</label>
    <select id={`iq-${name}`} value={value} onChange={e => onChange(e.target.value)} aria-invalid={!!error} aria-describedby={error ? `iq-${name}-err` : undefined}>
      <option value="">Select…</option>{options.map(o => <option key={o} value={o}>{o}</option>)}</select>
    {error && <p className="iq-error" id={`iq-${name}-err`}>{error}</p>}</div>;
}

export function NativeIntake() {
  const { pathname } = useLocation();
  const [step, setStep] = useState<0 | 1>(0);
  const [a, setA] = useState<IntakeAnswers>(empty);
  const [errors, setErrors] = useState<Errors>({});
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState("");
  const [done, setDone] = useState<{ name: string; email: string } | null>(null);
  const [hp, setHp] = useState("");
  const lock = useRef(false);
  const attempt = useRef(createSubmissionAttempt());
  const heading = useRef<HTMLHeadingElement>(null);
  const successRef = useRef<HTMLHeadingElement>(null);
  const firstRender = useRef(true);

  useEffect(() => { if (firstRender.current) { firstRender.current = false; return; } heading.current?.focus({ preventScroll: false }); }, [step]);
  useEffect(() => { if (done) successRef.current?.focus(); }, [done]);

  const set = <K extends keyof IntakeAnswers>(k: K, v: IntakeAnswers[K]) => { setA(p => ({ ...p, [k]: v })); setErrors(e => ({ ...e, [k]: undefined })); };
  const toggleChannel = (o: string) => {
    const has = a.channels.includes(o);
    const next = has ? a.channels.filter(c => c !== o) : o === "Not running ads" ? [o] : [...a.channels.filter(c => c !== "Not running ads"), o];
    set("channels", next);
  };
  const focusFirst = (errs: Errors, order: readonly string[]) => {
    const k = order.find(f => errs[f as keyof IntakeAnswers]);
    if (k) requestAnimationFrame(() => document.getElementById(`iq-${k}`)?.focus());
  };
  const next = () => {
    const errs = validate(a, STEP_ONE_FIELDS);
    setErrors(errs);
    if (Object.keys(errs).length) { focusFirst(errs, STEP_ONE_FIELDS); return; }
    setStep(1);
  };
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (lock.current || done) return;
    const errs = validate(a, [...STEP_ONE_FIELDS, ...STEP_TWO_FIELDS]);
    setErrors(errs);
    const stepOneBad = STEP_ONE_FIELDS.some(f => errs[f]);
    if (stepOneBad) { setStep(0); focusFirst(errs, STEP_ONE_FIELDS); return; }
    if (Object.keys(errs).length) { focusFirst(errs, STEP_TWO_FIELDS); return; }
    if (hp) { setServerError("We couldn't send your details. Please try again."); return; }
    lock.current = true; setBusy(true); setServerError("");
    const answers = Object.fromEntries(Object.entries({ ...a, website: normalizeWebsite(a.website) }).map(([k, v]) => [k, typeof v === "string" ? v.trim() : v])) as IntakeAnswers;
    const body = { answers, sourcePath: pathname.slice(0, 200) || "/", attribution: attribution(), hp: "" };
    const { id } = attempt.current(body);
    try {
      const { data, error } = await supabase.functions.invoke("submit-intake", { body: { ...body, submissionId: id } });
      if (error || !data?.success || data.receipt !== id) throw new Error("not accepted");
      trackLeadOnce(data.receipt, { content_name: "Strategy call intake" }, "Strategy call intake");
      setDone({ name: `${answers.firstName} ${answers.lastName}`, email: answers.email });
    } catch {
      setServerError("We couldn't save your details just now. Your answers are still here, so please try again.");
    } finally { lock.current = false; setBusy(false); }
  };

  if (done) return <div className="iq-card iq-done">
    <span className="iq-done-icon" aria-hidden="true"><Check size={22} /></span>
    <h3 ref={successRef} tabIndex={-1}>Your details are saved. Choose a time for your strategy call.</h3>
    <CalendlyInline name={done.name} email={done.email} title="TeleAds strategy call booking calendar" />
  </div>;

  return <form className="iq-card" onSubmit={submit} noValidate aria-busy={busy}>
    <div className="iq-progress"><span>Step {step + 1} of 2</span><span>{step === 0 ? "About your business" : "Your contact details"}</span></div>
    <div className="iq-bar" role="progressbar" aria-label="Intake progress" aria-valuemin={1} aria-valuemax={2} aria-valuenow={step + 1}><i style={{ width: step === 0 ? "50%" : "100%" }} /></div>
    <div className="iq-hp" aria-hidden="true"><label htmlFor="iq-fax">Fax</label><input id="iq-fax" tabIndex={-1} autoComplete="off" value={hp} onChange={e => setHp(e.target.value)} /></div>
    {step === 0 ? <div className="iq-step">
      <h3 ref={heading} tabIndex={-1}>About your business</h3>
      <Choice name="startingPoint" label="Where are you starting from?" options={STARTING_POINTS} value={a.startingPoint} onChange={v => set("startingPoint", v)} error={errors.startingPoint} />
      <div className="iq-row">
        <Text name="company" label="Company or brand" optional={a.startingPoint === "New brand" || !a.startingPoint} value={a.company} onChange={v => set("company", v)} error={errors.company} autoComplete="organization" />
        <Text name="website" label="Website" optional value={a.website} onChange={v => set("website", v)} error={errors.website} placeholder="yourbrand.com" inputMode="url" autoComplete="url" />
      </div>
      <Choice name="primaryNeed" label="What do you need most?" options={PRIMARY_NEEDS} value={a.primaryNeed} onChange={v => set("primaryNeed", v)} error={errors.primaryNeed} />
      <div className="iq-row">
        <Select name="adBudget" label="Monthly advertising budget" options={AD_BUDGETS} value={a.adBudget} onChange={v => set("adBudget", v)} error={errors.adBudget} />
        <Select name="revenue" label="Current monthly revenue" options={REVENUES} value={a.revenue} onChange={v => set("revenue", v)} error={errors.revenue} />
      </div>
      <Choice name="channels" label="Advertising channels" multi options={CHANNELS} value={a.channels} onChange={toggleChannel} error={errors.channels} />
      <Text name="heardFrom" label="How did you hear about us?" optional value={a.heardFrom} onChange={v => set("heardFrom", v)} error={errors.heardFrom} />
      <div className="iq-field"><label htmlFor="iq-goals">Goals or context<span className="iq-hint"> · optional</span></label>
        <textarea id="iq-goals" rows={4} maxLength={GOALS_MAX} value={a.goals} onChange={e => set("goals", e.target.value)} aria-invalid={!!errors.goals} aria-describedby="iq-goals-count" />
        <p className="iq-count" id="iq-goals-count">{a.goals.length}/{GOALS_MAX}</p>{errors.goals && <p className="iq-error">{errors.goals}</p>}</div>
      <div className="iq-actions"><Button type="button" className="iq-primary" onClick={next}>Next: contact details <ArrowRight size={16} /></Button></div>
    </div> : <div className="iq-step">
      <h3 ref={heading} tabIndex={-1}>Your contact details</h3>
      <div className="iq-row">
        <Text name="firstName" label="First name" value={a.firstName} onChange={v => set("firstName", v)} error={errors.firstName} autoComplete="given-name" />
        <Text name="lastName" label="Last name" value={a.lastName} onChange={v => set("lastName", v)} error={errors.lastName} autoComplete="family-name" />
      </div>
      <div className="iq-row">
        <Text name="email" type="email" label="Business email" value={a.email} onChange={v => set("email", v)} error={errors.email} autoComplete="email" inputMode="email" />
        <Text name="phone" type="tel" label="Phone" optional value={a.phone} onChange={v => set("phone", v)} error={errors.phone} autoComplete="tel" inputMode="tel" />
      </div>
      <p className="iq-notice">We use these details to contact you about your strategy call and this enquiry. See our <Link to="/privacy">Privacy Policy</Link> and <Link to="/terms">Terms</Link>.</p>
      {serverError && <p className="iq-server" role="alert">{serverError}</p>}
      <div className="iq-actions">
        <Button type="button" variant="ghost" className="iq-back" onClick={() => { setServerError(""); setStep(0); }} disabled={busy}><ArrowLeft size={16} /> Back</Button>
        <Button type="submit" className="iq-primary" disabled={busy}>{busy ? <><Loader2 className="iq-spin" size={16} /> Saving…</> : <>Continue to choose a time <ArrowRight size={16} /></>}</Button>
      </div>
    </div>}
  </form>;
}

export function IntakeAside() {
  return <aside className="iq-aside" aria-label="What happens next">
    <p className="acq-kicker">WHAT HAPPENS NEXT</p>
    <ol>
      <li><span>01</span><div><strong>Share your context</strong><p>Two short steps about your business and how to reach you.</p></div></li>
      <li><span>02</span><div><strong>Choose a time</strong><p>Pick a slot for your strategy call right after your details are saved.</p></div></li>
      <li><span>03</span><div><strong>Strategy call</strong><p>We review your offer, current acquisition and patient journey, then discuss the work that would make the biggest difference.</p></div></li>
    </ol>
    <div className="iq-aside-path" aria-hidden="true"><i /><i /><i /></div>
  </aside>;
}
